import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle2, XCircle, Send, Unplug, ArrowRight, AlertTriangle, AlertOctagon, CircleDot, Copy, Eye, EyeOff, FlaskConical, Info, RefreshCw, Rocket, Search, Timer } from 'lucide-react';
import { apiClient } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { ReactComponent as CircleCIIcon } from '../assets/icons/circleci-mono.svg';

const CircleCIIntegration: React.FC = () => {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [showSecret, setShowSecret] = useState(false);
  const [exampleProject, setExampleProject] = useState('');
  const [exampleChannel, setExampleChannel] = useState('');

  const [orgSlug, setOrgSlug] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [lastWebhookAt, setLastWebhookAt] = useState<string | null>(null);
  const [webhookEventCount, setWebhookEventCount] = useState(0);

  const [connectApiToken, setConnectApiToken] = useState('');
  const [connectOrgSlug, setConnectOrgSlug] = useState('');

  const webhookUrl = `${window.location.origin}/api/webhooks/circleci`;

  const fetchStatus = async () => {
    try {
      const data = await apiClient.getCircleCIStatus();
      if (data?.connected) {
        setConnected(true);
        setOrgSlug(data.org_slug || '');
        setWebhookSecret(data.webhook_secret || '');
        setLastWebhookAt(data.last_webhook_at || null);
        setWebhookEventCount(data.webhook_event_count || 0);
      } else {
        setConnected(false);
      }
    } catch {
      setConnected(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) fetchStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleConnect = async () => {
    setError(null);
    setSuccessMsg(null);
    setSaving(true);
    try {
      await apiClient.connectCircleCI({
        api_token: connectApiToken,
        org_slug: connectOrgSlug.trim() || undefined,
      });
      setSuccessMsg('CircleCI connected successfully!');
      setConnectApiToken('');
      await fetchStatus();
    } catch (err: any) {
      setError(err.response?.data || err.message || 'Failed to connect');
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnect = async () => {
    setError(null);
    setSuccessMsg(null);
    try {
      await apiClient.disconnectCircleCI();
      setConnected(false);
      setSuccessMsg('CircleCI disconnected.');
    } catch (err: any) {
      setError(err.response?.data || 'Failed to disconnect');
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const data = await apiClient.testCircleCI();
      if (data?.success) {
        setTestResult({ success: true, message: `Connected to CircleCI (${data.project_count ?? 0} project${data.project_count !== 1 ? 's' : ''} accessible)` });
      } else {
        setTestResult({ success: false, message: data?.error || 'Connection test failed' });
      }
    } catch (err: any) {
      setTestResult({ success: false, message: err.response?.data || 'Connection test failed' });
    } finally {
      setTesting(false);
    }
  };

  const copyValue = (key: string, value: string) => {
    navigator.clipboard.writeText(value);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-140px)]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {successMsg && (
        <Alert className="border-green-500 bg-green-50 dark:bg-green-950/30">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-700 dark:text-green-400">{successMsg}</AlertDescription>
        </Alert>
      )}
      {connected ? (
        <>
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <CircleCIIcon className="w-7 h-7 text-foreground" />
              <div>
                <CardTitle className="flex items-center gap-2">
                  CircleCI
                  <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-xs font-normal">Connected</Badge>
                </CardTitle>
                <CardDescription>Trigger pipelines, rerun failed workflows, fetch test results, react to pipeline failures, and run AI investigations against CircleCI</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              {orgSlug && (
                <div className="min-w-0">
                  <span className="text-muted-foreground">Organization</span>
                  <p className="font-medium truncate">{orgSlug}</p>
                </div>
              )}
              <div>
                <span className="text-muted-foreground">Webhook Events Received</span>
                <p className="font-medium">{webhookEventCount.toLocaleString()} event{webhookEventCount !== 1 ? 's' : ''}</p>
              </div>
              {lastWebhookAt && (
                <div>
                  <span className="text-muted-foreground">Last Webhook</span>
                  <p className="font-medium">{new Date(lastWebhookAt).toLocaleString()}</p>
                </div>
              )}
            </div>

            {testResult && (
              <Alert variant={testResult.success ? 'default' : 'destructive'} className={testResult.success ? 'border-green-500 bg-green-50 dark:bg-green-950/30' : ''}>
                {testResult.success ? <CheckCircle2 className="h-4 w-4 text-green-600" /> : <XCircle className="h-4 w-4" />}
                <AlertDescription className={testResult.success ? 'text-green-700 dark:text-green-400' : ''}>{testResult.message}</AlertDescription>
              </Alert>
            )}

            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleTest} disabled={testing} className="gap-1">
                {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                Test Connection
              </Button>
              <Button variant="outline" size="sm" onClick={handleDisconnect} className="gap-1">
                <Unplug className="w-3.5 h-3.5" /> Disconnect
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Webhook</CardTitle>
            <CardDescription>Register a CircleCI outbound webhook per project to trigger workflows on pipeline and job events. Actions work without this.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-sm">Webhook URL</Label>
              <div className="flex items-center gap-2 mt-1">
                <code className="flex-1 min-w-0 truncate bg-muted px-3 py-2 rounded text-sm font-mono">{webhookUrl}</code>
                <Button variant="outline" size="sm" onClick={() => copyValue('url', webhookUrl)} className="gap-1 shrink-0">
                  {copied === 'url' ? <CheckCircle2 className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied === 'url' ? 'Copied' : 'Copy'}
                </Button>
              </div>
            </div>
            {webhookSecret && (
              <div>
                <Label className="text-sm">Signing Secret</Label>
                <div className="flex items-center gap-2 mt-1">
                  <code className="flex-1 min-w-0 truncate bg-muted px-3 py-2 rounded text-sm font-mono">{showSecret ? webhookSecret : '••••••••••••••••••••••••'}</code>
                  <Button variant="outline" size="sm" onClick={() => setShowSecret(s => !s)} className="gap-1 shrink-0" aria-label={showSecret ? 'Hide secret' : 'Show secret'}>
                    {showSecret ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => copyValue('secret', webhookSecret)} className="gap-1 shrink-0">
                    {copied === 'secret' ? <CheckCircle2 className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
                    {copied === 'secret' ? 'Copied' : 'Copy'}
                  </Button>
                </div>
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              In the CircleCI dashboard, open each project's <strong className="text-foreground">Project Settings → Webhooks → Add Webhook</strong>, set the URL above, paste the signing secret, and select the <strong className="text-foreground">Workflow Completed</strong> and <strong className="text-foreground">Job Completed</strong> events. CircleCI signs each delivery with HMAC-SHA256 (<code className="bg-muted px-1 rounded text-[11px] font-mono">circleci-signature</code> header) and Kestrel verifies it.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Available Workflow Blocks</CardTitle>
            <CardDescription>Use these CircleCI actions and triggers in your workflows to automate CI/CD, flaky-build recovery, and failure response.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 mb-4">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Triggers (webhook)</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  { name: 'Workflow Failed', desc: 'Triggers when a workflow completes with failed or error status.', Icon: AlertTriangle },
                  { name: 'Workflow Succeeded', desc: 'Triggers when a workflow completes successfully.', Icon: CheckCircle2 },
                  { name: 'Workflow Completed (any status)', desc: 'Triggers when a workflow finishes with any status.', Icon: CircleDot },
                  { name: 'Job Failed', desc: 'Triggers when a job completes with failed status.', Icon: AlertOctagon },
                ].map(block => (
                  <div key={block.name} className="border rounded-lg p-3 space-y-1">
                    <div className="flex items-center gap-2">
                      <block.Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                      <span className="font-medium text-sm min-w-0 truncate">{block.name}</span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">{block.desc}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Actions</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  { name: 'Trigger Pipeline', desc: 'Trigger a pipeline on a branch or tag, optionally with parameters.', Icon: Rocket },
                  { name: 'Wait for Pipeline', desc: 'Wait for a pipeline\u2019s workflows to complete and return the result.', Icon: Timer },
                  { name: 'Get Workflow Status', desc: 'Fetch a pipeline\u2019s workflow statuses without waiting.', Icon: Info },
                  { name: 'Rerun Workflow', desc: 'Rerun a workflow, optionally only its failed jobs.', Icon: RefreshCw },
                  { name: 'Get Job Test Results', desc: 'Fetch a job\u2019s test results, summarizing failures.', Icon: FlaskConical },
                  { name: 'Investigate CircleCI', desc: 'AI investigates pipelines, workflows, jobs, and tests (read-only).', Icon: Search },
                ].map(block => (
                  <div key={block.name} className="border rounded-lg p-3 space-y-1">
                    <div className="flex items-center gap-2">
                      <block.Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                      <span className="font-medium text-sm min-w-0 truncate">{block.name}</span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">{block.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Example Workflow</CardTitle>
            <CardDescription>Click the highlighted fields below to customize, then create the workflow.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-muted/50 rounded-lg p-4 text-sm text-muted-foreground leading-loose">
              When a workflow fails in the{' '}
              <input
                value={exampleProject}
                onChange={e => setExampleProject(e.target.value)}
                placeholder="project name"
                style={{ width: `${Math.max(exampleProject.length || 12, 12)}ch` }}
                className="inline-block bg-cyan-200 dark:bg-cyan-800 text-cyan-900 dark:text-cyan-100 font-medium border border-cyan-400 dark:border-cyan-600 rounded px-1 py-0 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50 placeholder:text-cyan-600 dark:placeholder:text-cyan-300"
              />{' '}CircleCI project, rerun the failed jobs once, and if it fails again investigate with AI and post the findings to{' '}
              <input
                value={exampleChannel}
                onChange={e => setExampleChannel(e.target.value)}
                placeholder="#channel"
                style={{ width: `${Math.max(exampleChannel.length || 8, 8)}ch` }}
                className="inline-block bg-cyan-200 dark:bg-cyan-800 text-cyan-900 dark:text-cyan-100 font-medium border border-cyan-400 dark:border-cyan-600 rounded px-1 py-0 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50 placeholder:text-cyan-600 dark:placeholder:text-cyan-300"
              />{' '}on Slack.
            </div>
            <Button
              size="sm"
              className="gap-1.5"
              disabled={!exampleProject || !exampleChannel}
              onClick={() => navigate('/workflows/new', {
                state: {
                  suggestion: {
                    nl_prompt: `When a workflow fails in the ${exampleProject} CircleCI project, rerun the failed jobs once, and if it fails again investigate with AI and post the findings to ${exampleChannel} on Slack.`,
                  },
                },
              })}
            >
              Create This Workflow <ArrowRight className="w-3.5 h-3.5" />
            </Button>
          </CardContent>
        </Card>
        </>
      ) : (
        <>
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <CircleCIIcon className="w-7 h-7 text-foreground" />
              <div>
                <CardTitle>CircleCI</CardTitle>
                <CardDescription>Connect CircleCI to trigger pipelines, rerun failed workflows, fetch test results, react to pipeline failures, and run AI investigations in workflows.</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="circleci-token" className="text-sm">API Token</Label>
              <p className="text-xs text-muted-foreground mt-1 mb-2">
                Kestrel authenticates to the CircleCI API v2 with a <strong className="text-foreground">personal API token</strong> (<code className="bg-muted px-1 rounded text-[11px] font-mono">Circle-Token</code> header). Create one in the CircleCI dashboard under <strong className="text-foreground">User Settings → Personal API Tokens → Create New Token</strong>.
              </p>
              <Input
                id="circleci-token"
                type="password"
                value={connectApiToken}
                onChange={e => setConnectApiToken(e.target.value)}
                placeholder="CircleCI personal API token"
                className="mt-1 font-mono"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Stored encrypted and only used to authenticate requests to the CircleCI API.
              </p>
            </div>
            <div>
              <Label htmlFor="circleci-org" className="text-sm">Organization Slug (optional)</Label>
              <Input
                id="circleci-org"
                value={connectOrgSlug}
                onChange={e => setConnectOrgSlug(e.target.value)}
                placeholder="gh/my-org"
                className="mt-1 font-mono"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Used to scope project listings, e.g. <code className="bg-muted px-1 rounded text-[11px] font-mono">gh/my-org</code> or <code className="bg-muted px-1 rounded text-[11px] font-mono">circleci/&lt;org-id&gt;</code>. Find it under <strong className="text-foreground">Organization Settings → Overview</strong>.
              </p>
            </div>
            <Button onClick={handleConnect} disabled={saving || !connectApiToken}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Connect CircleCI
            </Button>
          </CardContent>
        </Card>
        </>
      )}
    </div>
  );
};

export default CircleCIIntegration;
